import { ScheduleHoursDTO, ScheduleHoursJornadaDTO, ScheduleHoursWeekdayDTO } from '../../dto/ServiceStatisticsDTO';

/** Cuántos minutos tiene una hora, para escalar el eje del gráfico. */
const HOUR = 60;
const DAY_MINUTES = 24 * HOUR;

/** `HH:mm` a minutos desde la medianoche. Un texto que no es una hora vale cero (el gráfico no se rompe). */
export function parseClock(text: string): number {
    const [hours, minutes] = text.split(':').map(Number);
    return Number.isFinite(hours) && Number.isFinite(minutes) ? hours * HOUR + minutes : 0;
}

/** Una fila del gráfico: un día de la semana (o un bloque del día) o una jornada, con las horas en minutos. */
export interface ScheduleRow {
    key: string;
    dayOfWeek: number;
    /** Rótulo del bloque ("Mañana"), solo en la vista por día de la semana. */
    blockLabel: string | null;
    /** `yyyy-MM-dd`, solo en la vista por jornada. */
    date: string | null;
    scheduledFromText: string;
    scheduledToText: string;
    firstServedText: string;
    lastCompletedText: string;
    scheduledFrom: number;
    scheduledTo: number;
    firstServed: number;
    lastCompleted: number;
    /** Terminó después del cierre más que la tolerancia. */
    late: boolean;
    /** Minutos que se pasó del cierre (solo con `late`). */
    overrunMinutes: number;
    /** Jornadas del día de la semana y cuántas terminaron tarde; solo en la vista por día de la semana. */
    days: number;
    overrunDays: number;
    overbooked: boolean;
}

function weekdayRow(item: ScheduleHoursWeekdayDTO, index: number, tolerance: number): ScheduleRow {
    return {
        key: `w${item.dayOfWeek}-${item.scheduledFrom}-${index}`,
        dayOfWeek: item.dayOfWeek,
        blockLabel: item.blockLabel,
        date: null,
        scheduledFromText: item.scheduledFrom,
        scheduledToText: item.scheduledTo,
        firstServedText: item.avgFirstServed,
        lastCompletedText: item.avgLastCompleted,
        scheduledFrom: parseClock(item.scheduledFrom),
        scheduledTo: parseClock(item.scheduledTo),
        firstServed: parseClock(item.avgFirstServed),
        lastCompleted: parseClock(item.avgLastCompleted),
        late: item.overrunDays > 0 && item.avgOverrunMinutes > tolerance,
        overrunMinutes: Math.round(item.avgOverrunMinutes),
        days: item.days,
        overrunDays: item.overrunDays,
        overbooked: false,
    };
}

function jornadaRow(item: ScheduleHoursJornadaDTO, index: number, tolerance: number): ScheduleRow {
    return {
        key: `j${item.date}-${index}`,
        dayOfWeek: item.dayOfWeek,
        blockLabel: null,
        date: item.date,
        scheduledFromText: item.scheduledFrom,
        scheduledToText: item.scheduledTo,
        firstServedText: item.firstServed,
        lastCompletedText: item.lastCompleted,
        scheduledFrom: parseClock(item.scheduledFrom),
        scheduledTo: parseClock(item.scheduledTo),
        firstServed: parseClock(item.firstServed),
        lastCompleted: parseClock(item.lastCompleted),
        late: item.overrunMinutes > tolerance,
        overrunMinutes: Math.round(item.overrunMinutes),
        days: 1,
        overrunDays: item.overrunMinutes > tolerance ? 1 : 0,
        overbooked: item.overbooked,
    };
}

/** Los días de la semana de lunes a domingo (y los bloques de un día por hora de apertura), tal como los trae el backend. */
export function buildWeekdayRows(data: ScheduleHoursDTO): ScheduleRow[] {
    return [...data.weekdays]
        .sort((a, b) => a.dayOfWeek - b.dayOfWeek || parseClock(a.scheduledFrom) - parseClock(b.scheduledFrom))
        .map((item, index) => weekdayRow(item, index, data.toleranceMinutes));
}

/** Las jornadas en el orden en que llegan (las más recientes primero). */
export function buildJornadaRows(data: ScheduleHoursDTO): ScheduleRow[] {
    return data.jornadas.map((item, index) => jornadaRow(item, index, data.toleranceMinutes));
}

/**
 * El rango del eje: desde una hora antes de lo más temprano (apertura o primer atendido) hasta una hora después de lo más
 * tardío (cierre o última atención), redondeado a la hora. Nunca sale del día.
 */
export function scheduleDomain(rows: ScheduleRow[]): { start: number; end: number } {
    const earliest = Math.min(...rows.map(row => Math.min(row.scheduledFrom, row.firstServed)));
    const latest = Math.max(...rows.map(row => Math.max(row.scheduledTo, row.lastCompleted)));
    const start = Math.max(0, Math.floor((earliest - HOUR) / HOUR) * HOUR);
    const end = Math.min(DAY_MINUTES, Math.ceil((latest + HOUR) / HOUR) * HOUR);
    return { start, end: Math.max(end, start + 2 * HOUR) };
}

/** Las marcas del eje: una por hora entera dentro del rango. */
export function hourMarks(domain: { start: number; end: number }): number[] {
    const marks: number[] = [];
    for (let minute = domain.start; minute <= domain.end; minute += HOUR) marks.push(minute);
    return marks;
}

export type ScheduleSummary =
    | { kind: 'onTime' }
    /** Un solo cierre en toda la agenda: se puede decir a qué hora cierra y a qué hora termina. */
    | { kind: 'late'; closeMinute: number; lastMinute: number; minutes: number; days: number; total: number }
    /** Cierres distintos según el día o el bloque: solo cuánto se pasa. */
    | { kind: 'lateMixed'; minutes: number; days: number; total: number };

/**
 * La conclusión de toda la agenda, con los mismos números de la tabla de servicios: jornadas que terminaron tarde (de las
 * que tuvieron atenciones) y promedio del exceso en esas jornadas, ponderado por la cantidad de jornadas de cada fila.
 * Dentro de la tolerancia se considera que termina en horario.
 */
export function summarizeSchedule(data: ScheduleHoursDTO): ScheduleSummary {
    // Una jornada es un día, no un bloque: de cada día de la semana cuenta el bloque que cierra último (el que define si
    // se terminó tarde), así un día con mañana y tarde no se cuenta dos veces
    const closing = new Map<number, ScheduleHoursWeekdayDTO>();
    for (const item of data.weekdays) {
        const current = closing.get(item.dayOfWeek);
        if (!current || parseClock(item.scheduledTo) > parseClock(current.scheduledTo)) closing.set(item.dayOfWeek, item);
    }
    const rows = [...closing.values()];
    const total = rows.reduce((sum, item) => sum + item.days, 0);
    const lateDays = rows.reduce((sum, item) => sum + item.overrunDays, 0);
    if (lateDays <= 0) return { kind: 'onTime' };
    const average = rows.reduce((sum, item) => sum + item.overrunDays * item.avgOverrunMinutes, 0) / lateDays;
    if (average <= data.toleranceMinutes) return { kind: 'onTime' };
    const minutes = Math.round(average);
    const closes = new Set(rows.map(item => item.scheduledTo));
    if (closes.size !== 1) return { kind: 'lateMixed', minutes, days: lateDays, total };
    const closeMinute = parseClock(rows[0].scheduledTo);
    return { kind: 'late', closeMinute, lastMinute: closeMinute + minutes, minutes, days: lateDays, total };
}

/** `dd/MM` de una fecha `yyyy-MM-dd`. */
export function shortDay(isoDate: string): string {
    const [, month, day] = isoDate.split('-');
    return `${day}/${month}`;
}
